import type React from 'react';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import type { MessageFormatter, RichTextFormatter } from '@/core/localization/translate';
import { pageTypeSelector } from '@/domains/core/page/page.selectors';
import { Link } from '@/domains/core/routing/components/Link';
import { routes } from '@/domains/core/routing/routes/routes.default';
import { isB2B } from '@/domains/core/settings/utils';
import { Button } from '@/domains/core/tamagoshiTailwind/components/Button/Button';
import { InputText } from '@/domains/core/tamagoshiTailwind/components/Input/InputText';
import { Typography } from '@/domains/core/tamagoshiTailwind/components/Typography/Typography';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { getIntersectionPosition } from '@/domains/digitalAdvice/GuidePage/utils/intersectionObserver.util';
import { GTM_DISPLAY_NEWSLETTER_FOOTER } from '@/domains/retention/Footer/tracking/gtmEvents';
import { useNewsletter } from '@/domains/retention/NewsletterModal/hooks/useNewsletter';
import { getError } from '@/domains/retention/NewsletterModal/utils/getError';
import { SubscribeSource } from '@/domains/retention/UserProfiling/interfaces/Channel';
import { useTranslationsFooterContactNewsletter } from '../../hooks/useTranslations';
import { footerContactNewsletterSubmitText } from '../../translations';
import { NewsletterTitle } from './NewsletterTitle';
export interface NewsletterProps {
  theme?: 'pro' | 'default';
}
function brFormatter() {
  let index = 0;
  return {
    br: () => <br key={index++} />
  };
}
export const Newsletter = ({
  theme
}: NewsletterProps) => {
  const translations = useTranslationsFooterContactNewsletter();
  const {
    error,
    emailAddress,
    onFocus,
    onChange,
    onSubscribeNewsletter
  } = useNewsletter();
  const pageType = useSelector(pageTypeSelector);
  const onDisplay = () => {
    Gtm.push(GTM_DISPLAY_NEWSLETTER_FOOTER(pageType || 'unknown'));
  };
  const subscribe = (e: React.SyntheticEvent) => {
    onSubscribeNewsletter(e, false, SubscribeSource.FOOTER);
  };
  return <div id="newsletterContainer" className="relative flex justify-center bg-foundation-inverted px-m py-l
        text-foundation-primary desktop:px-0">
      <div className="desktop:relative" data-is-b2b={isB2B() || theme === 'pro'}>
        <div className="w-full min-[672px]:w-[672px]">
          <NewsletterTitle />
          <InView as="div" triggerOnce onChange={(inView, entry) => {
          if (inView) {
            getIntersectionPosition(entry);
            onDisplay();
          }
        }}>
            <form className="mx-0 mb-s mt-m flex flex-row gap-s" id="newsletter-form" onSubmit={subscribe}>
              <div className="flex w-full flex-col gap-s tablet:flex-row">
                <InputText id="footerNewsletterEmailInput" name="email" value={emailAddress} label={translations.inputLabel} onChange={onChange} onFocus={onFocus} helperText={getError(error).message ? getError(error).message : ''} status={error ? 'error' : 'default'} type="text" />
                <Button className="w-auto tablet:w-[25%] wide:w-[16%]" fitContent onClick={subscribe} size="L" text={footerContactNewsletterSubmitText} variant="primary" />
              </div>
            </form>
          </InView>
          <div className="text-center" data-testid="newsletter-subtext">
            <Typography as="span" scale="small" weight="regular" className="text-foundation-primary">
              {(translations.subtext as MessageFormatter<{
              br: RichTextFormatter;
            }>).format(brFormatter())}
              &nbsp;
              <Link color="light" href={routes.staticDataProtection} tag="a" target="_blank">
                {translations.subtextLink}
              </Link>
            </Typography>
          </div>
        </div>
      </div>
    </div>;
};
Newsletter.displayName = 'Newsletter';